import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Badge component - Minimal React wrapper for behavioral concerns.
 *
 * All visual styling is applied via CSS classes. This wrapper provides:
 * - `data-component="Badge"` for component identification
 * - Ref forwarding for tooltip/popover compatibility
 * - `className` merging via `cn()`
 *
 * The `.badge` base class is applied automatically — `className` composes
 * modifiers and utilities on top of it.
 *
 * @example
 * ```tsx
 * // Default text badge (gets .badge .badge-default)
 * <Badge>NEW</Badge>
 *
 * // Variant + size
 * <Badge className="badge-good badge-sm">ACTIVE</Badge>
 *
 * // With icon child (consumer applies badge-icon class)
 * <Badge className="badge-critical">
 *   <AlertCircle className="badge-icon" />
 *   ALERT
 * </Badge>
 *
 * // Pill number badge
 * <Badge className="badge-pill badge-sm" role="status" aria-label="3 unread">3</Badge>
 *
 * // Outlined pill
 * <Badge className="badge-pill badge-xl badge-outline badge-default">12</Badge>
 *
 * // Dynamic variant
 * <Badge className={cn(ok ? 'badge-good' : 'badge-critical')}>
 *   {ok ? 'ON' : 'OFF'}
 * </Badge>
 * ```
 */

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement>;

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <span ref={ref} data-component="Badge" className={cn('badge', className)} {...props}>
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';
